import {
  Divider,
  MenuItem,
  Stack,
  Switch,
  ToggleButtonGroup,
} from "@mui/material";

import { Row, Label, SwitchRow } from "../index.styles";
import { DecimalInput, Select, ToggleButton } from "../../../components";

import { selectGeneralSettings } from "../../../features/settings/settings.selector";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { IGeneralSettingsState } from "../../../features/settings/settings.types";
import { selectExtension } from "../../../features/extension/extension.selector";
import { updateGeneralSettingsThunk } from "../../../features/settings/settings.thunk";
import { TInboundPlacementService } from "../../../features/feeSettings/feeSettingsSlice";
import { TFulfilmentType } from "../../BulkAnalyzer/FulfilmentDropdown";

const GeneralSettings = () => {
  const dispatch = useAppDispatch();
  const generalSettings = useAppSelector(selectGeneralSettings);
  const { isExtension } = useAppSelector(selectExtension);

  if (!generalSettings) return null;

  return (
    <Stack spacing="8px" width={{ xs: "100%", md: "50%" }} margin={"auto"}>
      <Divider>Amazon Specific</Divider>

      <Row>
        <Label tooltip="Your FBA inbound shipping rates to include in calculations.">
          Inbound FBA Shipping
        </Label>
        <DecimalInput
          prefix="$"
          suffix="/ pound"
          value={generalSettings.inboundShipping}
          onChange={(inboundShipping: number) =>
            dispatch(updateGeneralSettingsThunk({ inboundShipping }))
          }
          align="right"
        />
      </Row>

      <Row>
        <Label tooltip="The FBA inbound placement services, fees are estimated accordingly.">
          Inbound Placement
        </Label>
        <Select
          value={generalSettings.inboundPlacement}
          onChange={(e) =>
            dispatch(
              updateGeneralSettingsThunk({
                inboundPlacement: e.target.value as TInboundPlacementService,
              }),
            )
          }
          size="large"
          fullWidth
        >
          <MenuItem value="Optimized">Amazon Optimized Splits</MenuItem>
          <MenuItem value="Partial">Partial Shipment Splits</MenuItem>
          <MenuItem value="Minimal">Minimal Shipment Splits</MenuItem>
        </Select>
      </Row>

      <Row>
        <Label tooltip="Expected FBA storage time to be included in the calculations. Can be manually adjusted as you analyze.">
          Storage Time
        </Label>
        <DecimalInput
          suffix="months"
          value={generalSettings.storageTime}
          onChange={(storageTime: number) =>
            dispatch(updateGeneralSettingsThunk({ storageTime }))
          }
          align="right"
        />
      </Row>

      <Row>
        <Label tooltip="Default fulfillment type (FBA/FBM) for calculations. Can be manually adjusted as you analyze.">
          Local Fulfilment
        </Label>
        <ToggleButtonGroup
          value={generalSettings.fulfilment}
          onChange={(_e, value) =>
            dispatch(
              updateGeneralSettingsThunk({
                fulfilment: value as TFulfilmentType,
              }),
            )
          }
          exclusive
        >
          <ToggleButton size="large" value="FBA">
            FBA
          </ToggleButton>
          <ToggleButton size="large" value="FBM">
            FBM
          </ToggleButton>
        </ToggleButtonGroup>
      </Row>

      <Divider sx={{ pt: 2 }}>Additional Costs</Divider>

      <Row>
        <Label tooltip="Fees if using a prep center for FBA, to include in calculations.">
          Prep Fee
        </Label>
        <DecimalInput
          prefix="$"
          value={generalSettings.prepFee}
          onChange={(prepFee: number) =>
            dispatch(updateGeneralSettingsThunk({ prepFee }))
          }
          align="right"
        />
      </Row>

      <Row>
        <Label tooltip="Any other per item costs to include in calculations.">
          Miscellaneous Fee
        </Label>
        <DecimalInput
          prefix="$"
          value={generalSettings.miscFee}
          onChange={(miscFee: number) =>
            dispatch(updateGeneralSettingsThunk({ miscFee }))
          }
          align="right"
        />
      </Row>

      <Row>
        <Label tooltip="Any other per item cost, as a percentage of the product cost, to include in calculations.">
          Miscellaneous Fee (% of cost)
        </Label>
        <DecimalInput
          suffix="%"
          value={generalSettings.miscFeePercent}
          onChange={(miscFeePercent: number) =>
            dispatch(updateGeneralSettingsThunk({ miscFeePercent }))
          }
          align="right"
        />
      </Row>

      {isExtension && (
        <>
          <Divider sx={{ pt: 2 }}>Chrome Extension</Divider>

          <SwitchRow>
            <Label tooltip="Automatically opens extension window in amazon product page.">
              Auto Open Extension
            </Label>
            <Switch
              checked={generalSettings.autoOpenExtension}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateGeneralSettingsThunk({ autoOpenExtension: checked }),
                );
                if (isExtension) {
                  chrome.runtime.sendMessage({
                    type: "set_auto_open",
                    autoOpen: checked,
                  });
                }
              }}
            />
          </SwitchRow>
        </>
      )}

      {/* <Divider sx={{ pt: 2 }}>Miscellaneous</Divider>

      <Row>
        <Label tooltip="Display Top Offers table on the search results page.">
          Top Offers on Search Results
        </Label>
        <Switch />
      </Row>

      <Row>
        <Label tooltip="On wider screens display the Keepa chart on search results pages.">
          Keepa on Search Results
        </Label>
        <Switch />
      </Row>

      <Row>
        <Label tooltip="Stores the geographic location when scanning using the mobile apps.">
          Store Geo Location
        </Label>
        <Switch />
      </Row>

      <Row>
        <Label tooltip="Replaces the white interface with black and shades of gray for all SAS platforms.">
          Dark Mode (Beta)
        </Label>
        <Switch />
      </Row> */}
    </Stack>
  );
};

export default GeneralSettings;
